<template>
  <div class='role-details'>
    <dean-two-column-layout :title="headerTitle">
      <dean-section-title-item
          :icon="saveIcon"
          slot="title-items"
          :content="true"
          :large="true"
          @click.native="save"
      >
        Opslaan
      </dean-section-title-item>
      <div class="column" slot="left">
        <dean-form-row id="name" label="Rol naam" v-model="data.name" type="text" :required-field="true"
                       :warning="$v.data.name.$error" @focus="$v.data.name.$touch()"/>

        <dean-select-form-row id="type" label="Rol type" placeholder="kies een role type"
                              :value="data.type"
                              :options="typesOptions"
                              @input="valueChanged($event, 'type')"
                              :requiredField="true"></dean-select-form-row>

        <dean-form-row id="level" label="Rol level" v-model="data.level" type="text" :required-field="true"
                       :warning="$v.data.level.$error" @focus="$v.data.level.$touch()"/>

        <div v-if="$route.params.id">
          <div class="dean-button-row row">
            <div class="small-6 medium-6 large-6 hd-6 columns">
              <p>Privileges</p>
            </div>
            <div class="small-6 medium-6 large-6 hd-6 columns dean-button-column">
              <dean-button id="changePrivileges" @click.native="routeTo('auth.admin.role.privileges')">
                Privileges wijzigen
              </dean-button>
            </div>
          </div>
        </div>
      </div>
    </dean-two-column-layout>
  </div>
</template>

<script lang="ts">
  import {Component} from 'vue-property-decorator';
  import axios from 'axios';
  import {
    deanSectionTitle,
    deanSectionTitleItem,
    saveIcon,
    warningIcon,
  } from '@deanone/dean-components';
  import DeanFormRow from '@/components/deanComponents/deanFormRow.vue';
  import DeanSelectFormRow from '@/components/deanComponents/deanSelectFormRow.vue';
  import DeanTwoColumnLayout from '@/components/deanComponents/deanTwoColumnLayout.vue';
  import {validationMixin} from 'vuelidate';
  import {required} from 'vuelidate/lib/validators';
  import {Validations} from 'vuelidate-property-decorators';
  import {createNotification} from '@/mixins/validations';
  import {mixins} from 'vue-class-component';
  import sideMenuAdminMixin from '@/mixins/sideMenuAdminMixin';
  import DeanButton from '@/components/deanComponents/deanButton.vue';

  @Component({
    components: {
      DeanButton,
      deanSectionTitle,
      deanSectionTitleItem,
      DeanFormRow,
      DeanSelectFormRow,
      DeanTwoColumnLayout,
    },
    mixins: [validationMixin],
  })
  export default class Details extends mixins(sideMenuAdminMixin) {
    // DATA
    public sideMenuTitle: string = 'Rol';
    public data: any = {
      name: '',
      type: '',
      level: 0,
    };
    public saveIcon: any = saveIcon;

    public typesOptions: Array<{ label: string, value: string }> = [
      {
        label: 'Business',
        value: 'business',
      },
      {
        label: 'Partner',
        value: 'partner',
      },
    ];

    // COMPUTED
    @Validations()
    get validations() {
      return {
        data: {
          name: {required},
          type: {required},
          level: {required},
        },
      };
    }

    get headerTitle(): string {
      return (this.$route.params.id !== undefined) ? 'Rol wijzigen' : 'Rol toevoegen';
    }

    // METHODS
    public save() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.$store.dispatch('setLoadOverlay', true);

        if (this.$route.params.id) {
          this.update();
        } else {
          this.create();
        }
      } else {
        this.$toast(createNotification(this.$v));
      }
    }

    public create() {
      const requestUrl = '/-/trade/api/admin.role.create';
      const requestData = {
        name: this.data.name,
        type: this.data.type,
        level: this.data.level,
      };

      axios.post(requestUrl, requestData).then(
        (response) => {
          this.$toast(this.$store.state.main.notification.messages.dataSaved);
        },
        (error) => {
          if (
              error.response.data.errors.code === 'ERR_NAME_ALREADY_EXISTS'
          ) {
            this.$toast({
              icon: warningIcon,
              text: 'De rolnaam bestaat al. Kies alstublieft een andere.',
              theme: 'warning',
              title: 'Fout',
            });
          } else {
            this.$toast(this.$store.state.main.notification.messages.couldNotSendData);
          }
        },
      ).finally(() => {
        this.$store.dispatch('setLoadOverlay', false);
      });
    }

    public update() {
      const requestUrl = '/-/trade/api/admin.role.update';
      const requestData = {
        id: this.$route.params.id,
        name: this.data.name,
        type: this.data.type,
        level: this.data.level,
      };

      axios.post(requestUrl, requestData).then(
        (response) => {
          this.$toast(this.$store.state.main.notification.messages.dataSaved);
        },
        (error) => {
          if (
            error.response.data.errors.code === 'ERR_NAME_ALREADY_EXISTS'
          ) {
            this.$toast({
              icon: warningIcon,
              text: 'De rolnaam bestaat al. Kies alstublieft een andere.',
              theme: 'warning',
              title: 'Fout',
            });
          } else {
            this.$toast(this.$store.state.main.notification.messages.couldNotSendData);
          }
        },
      ).finally(() => {
        this.$store.dispatch('setLoadOverlay', false);
      });
    }

    public routeTo(name: string) {
      this.$router.push({name});
    }

    public created() {
      this.$store.dispatch('setLoadOverlay', true);

      if (this.$route.params.id) {
        const requestUrl = '/-/trade/api/admin.role.info';
        const requestData = {
          id: this.$route.params.id,
        };

        axios.post(requestUrl, requestData).then(
          (response) => {
            this.data = response.data;
            this.sideMenuTitle = 'Rol ' + response.data.name;
          },
          (error) => {
            this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
          },
        ).finally(() => {
          this.$store.dispatch('setLoadOverlay', false);
          this.buildSideMenuAdmin(this.sideMenuTitle, this.sideMenuRoleSection);
        });
      } else {
        this.$store.dispatch('setLoadOverlay', false);
        this.buildSideMenuAdmin();
      }
    }

    public valueChanged(field: any, id: any) {
      this.data[id] = field.value;
    }
  }
</script>

<style lang="scss" scoped>
  @import '~@deanone/dean-components/src/scss/vars';

  :deep(.dean-button-row) {
    padding-left: 10px;

    :deep(.dean-button-column) {
      height: 50px;
      padding: 6px 10px 6px 0;
    }
  }
</style>
